import { Injectable } from '@angular/core';
import { V1ExternalAccountInfo } from '@blk/portfolio-security-server/accountInfo';
import { PortfolioEditorMode } from '@ddt-web/config';
import { PasAsset } from '@ddt-web/pas';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AccountErrors, PartnerAccount, PartnerAcknowledgement, PartnerAuthentication, PartnerAuthenticationInfo, PartnerPlatform } from '../../models';
import { ImportPartnerPortfoliosState, ProposalState } from '../../store';
import { AnalysisState } from '../../store/application-state/analysis.state';
import * as appAction from '../../store/application-state/application-state.actions';
import { ApplicationState } from '../../store/application-state/application-state.reducer';
import * as appStateSelectors from '../../store/application-state/application-state.selector';
import { PerformanceState } from '../../store/application-state/performance.state';

@Injectable()
export class ApplicationStateStoreProxyService {
	constructor(public store: Store<ApplicationState>) { }
	public getApbState(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getApbState));
	}

	public getProposalState(): Observable<ProposalState> {
		return this.store.pipe(select(appStateSelectors.getProposalState));
	}

	public getPeState(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getPEState));
	}

	public updateApbState(changes: any): void {
		this.store.dispatch(new appAction.UpdateApbModalVisibilityAction(changes));
	}

	public updateProposalPortfolioName(changes: any): void {
		this.store.dispatch(new appAction.UpdateProposalPortfolioNameAction(changes));
	}

	public updateProposalMetrics(changes: any): void {
		this.store.dispatch(new appAction.UpdateProposalMetricsAction(changes));
	}

	public updateProposalPurpose(changes: any): void {
		this.store.dispatch(new appAction.UpdateProposalPurposeAction(changes));
	}

	public updateHypotheticalGrowthMultiplier(changes: any): void {
		this.store.dispatch(new appAction.UpdateHypotheticalGrowthMultiplierAction(changes));
	}

	public updateHypotheticalGrowthStart(changes: { hypotheticalGrowthStartTimestamp: number }): void {
		this.store.dispatch(new appAction.UpdateHypotheticalGrowthStartAction(changes));
	}

	public updateCustomAdvisoryFee(changes: PerformanceState): void {
		this.store.dispatch(new appAction.UpdateCustomAdvisoryFee(changes));
	}

	public updateSelectedBenchmarkForCaptureRatio(changes: any): void {
		this.store.dispatch(new appAction.UpdateSelectedBenchmarkForCaptureRatio(changes));
	}

	public updateKeyRateSelectedCurrency(changes: any): void {
		this.store.dispatch(new appAction.UpdateKeyRateSelectedCurrencyAction(changes));
	}

	public getPerformanceState(): Observable<PerformanceState> {
		return this.store.pipe(select(appStateSelectors.getPerformanceState));
	}

	public getRiskState(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getRiskState));
	}

	public getApplicationState(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getApplicationState));
	}

	public updateFundsToExplore(changes: any): void {
		this.store.dispatch(new appAction.UpdateFundsToExploreAction(changes));
	}

	public getFundsToExplore(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getFundsToExploreState));
	}

	public updatePortfolioIds(changes: any): void {
		this.store.dispatch(new appAction.UpdatePortfolioIdsAction(changes));
	}

	public getPortfolioIds(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getPortfolioIdsState));
	}

	public updateSelectedPortfolioIndex(changes: any): void {
		this.store.dispatch(new appAction.UpdateSelectedPortfolioIndexAction(changes));
	}

	public getSelectedPortfolioIndex(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getSelectedPortfolioIndexState));
	}

	public updateResumeExcelDownload(changes: any): void {
		this.store.dispatch(new appAction.UpdateResumeExcelDownloadAction(changes));
	}

	public getResumeExcelDownload(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getResumeExcelDownloadState));
	}

	public updateGenerateReportData(changes: any): void {
		this.store.dispatch(new appAction.UpdateGenerateReportDataAction(changes));
	}

	public updateFeatureFlags(changes: any): void {
		this.store.dispatch(new appAction.UpdateFeatureFlagsAction(changes));
	}

	public updateAnalysisState(changes: AnalysisState): void {
		this.store.dispatch(new appAction.UpdateAnalysisStateAction(changes));
	}

	public getAnalysisState(): Observable<AnalysisState> {
		return this.store.pipe(select(appStateSelectors.getAnalysisState));
	}

	public getSelectedCurrency(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getAnalysisStateCurrency));
	}

	public getSelectedBenchmark(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getAnalysisStateBenchmark));
	}

	public updateCmaIsTransientPortfolioEdited(changes: any): void {
		this.store.dispatch(new appAction.UpdateCmaIsTransientPortfolioEdited(changes));
	}

	public getCmaIsTransientPortfolioEdited(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getCmaIsTransientPortfolioEdited));
	}

	public updateComparisonIdsPortfolioEditor(ids: Array<string>): void {
		this.store.dispatch(new appAction.UpdatePEModelComparisonIdsAction(ids));
	}

	public clearComparisonIdsPortfolioEditor(): void {
		this.store.dispatch(new appAction.ClearPEModelComparisonIdsAction());
	}

	public getComparisonIdsPortfolioEditor$(): Observable<string[]> {
		return this.store.pipe(select(appStateSelectors.getComparisonIdsForPEModel));
	}

	public getEngagementIdPortfolioEditor$(): Observable<string> {
		return this.store.pipe(select(appStateSelectors.getEngagementIdForPEModel));
	}

	public updateEngagementIdPortfolioEditor(id: string): void {
		this.store.dispatch(new appAction.UpdatePEModelEngagementIdAction(id));
	}

	public clearEngagementIdPortfolioEditor(): void {
		this.store.dispatch(new appAction.ClearPEModelEngagementIdAction());
	}

	public getAssetClassesPortfolioEditor$(): Observable<string[]> {
		return this.store.pipe(select(appStateSelectors.getAssetClassesForPEModel));
	}

	public updateAssetClassesPortfolioEditor(ids: string[]): void {
		this.store.dispatch(new appAction.UpdatePEModelAssetClassesAction(ids));
	}

	public updateStressTestsPortfolioEditor(ids: string[]): void {
		this.store.dispatch(new appAction.UpdatePEModelStressTestsAction(ids));
	}

	public getStressTestsPortfolioEditor$(): Observable<string[]> {
		return this.store.pipe(select(appStateSelectors.getStressTestsForPEModel));
	}

	public updateAcBenchmarkPortfolioEditor(acBenchmark: string): void {
		this.store.dispatch(new appAction.UpdatePEModelAcBenchmarkAction(acBenchmark));
	}

	public getAcBenchmarkPortfolioEditor$(): Observable<string> {
		return this.store.pipe(select(appStateSelectors.getAcBenchmarkForPEModel));
	}

	public updateAssetTypesPortfolioEditor(assetTypes: { displayName: string; label?: string; count?: number }[]): void {
		this.store.dispatch(new appAction.UpdatePEModelAssetTypesAction(assetTypes));
	}

	public getAssetTypesPortfolioEditor$(): Observable<{ displayName: string; label?: string; count?: number }[]> {
		return this.store.pipe(select(appStateSelectors.getAssetTypesForPEModel));
	}

	public updateUrlIdsPortfolioEditor(ids: string[]): void {
		this.store.dispatch(new appAction.UpdatePEModelUrlIdsAction(ids));
	}

	public getUrlIdsPortfolioEditor$(): Observable<string[]> {
		return this.store.pipe(select(appStateSelectors.getUrlIdsForPEModel));
	}

	public updatePEModePortfolioEditor(mode: PortfolioEditorMode): void {
		this.store.dispatch(new appAction.UpdatePEModelPEModeAction(mode));
	}

	public getPEModePortfolioEditor$(): Observable<PortfolioEditorMode> {
		return this.store.pipe(select(appStateSelectors.getPEModeForPEModel));
	}

	public updateIretireUserDataState(changes: any): void {
		this.store.dispatch(new appAction.UpdateIretireUserDataState(changes));
	}

	public getIretireUserDataState$(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getIretireUserData));
	}

	public clearIretireUserData(): void {
		this.store.dispatch(new appAction.ClearIretireUserDataAction());
	}
	public updateIqStatusOrAof(statusOrAof: string): void {
		this.store.dispatch(new appAction.UpdateIqStatusOrAreaOfFocus(statusOrAof));
	}

	public getIqStatusOrAof(): Observable<string> {
		return this.store.pipe(select(appStateSelectors.getAnalysisStateIqStatusOrAoF));
	}

	public updateAnalysedPortfoliosTaxQualifiedType(analysisType: string): void {
		this.store.dispatch(new appAction.UpdatePortfoliosTaxQualifiedType(analysisType));
	}

	public getAnalysedPortfoliosTaxQualifiedType(): Observable<string> {
		return this.store.pipe(select(appStateSelectors.getAnalysedPortfoliosTaxQualifiedType));
	}

	public getMeSelectedModelsData$(): Observable<any> {
		return this.store.pipe(select(appStateSelectors.getMeSelectedModelsData));
	}

	public updateMeSelectedModelsData(changes: any): void {
		this.store.dispatch(new appAction.UpdateMeSelectedModelsData(changes));
	}

	public getIsDismissedFundsUpdated(): Observable<boolean> {
		return this.store.pipe(select(appStateSelectors.getIsDismissedFundsUpdated));
	}

	public updateIsDismissedFundsUpdated(isDismissedFundsUpdated: boolean): void {
		this.store.dispatch(new appAction.UpdateIsDismissedFundsUpdated(isDismissedFundsUpdated));
	}

	public getImportPartnerPortfoliosState(): Observable<ImportPartnerPortfoliosState> {
		return this.store.pipe(select(appStateSelectors.getImportPartnerPortfoliosState));
	}

	public resetImportPartnerPortfoliosState() {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosState(new ImportPartnerPortfoliosState()));
	}

	public updateImportPartnerPortfoliosState(importPartnerPortfoliosState: ImportPartnerPortfoliosState) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosState(importPartnerPortfoliosState));
	}

	public updateImportPartnerPortfoliosPartnerPlatform(partnerPlatform: PartnerPlatform) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosPartnerPlatform(partnerPlatform));
	}

	public updateImportPartnerPortfoliosPortfolioData(portfolioData: PartnerAccount[]) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosPortfolioData(portfolioData));
	}

	public updateImportPartnerPortfoliosSelectedPortfolios(selectedPortfolios: Map<string, V1ExternalAccountInfo>) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosSelectedPortfolios(selectedPortfolios));
	}

	public updateImportPartnerPortfoliosIsRefreshNeeded(isRefreshNeeded: boolean) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosIsRefreshNeeded(isRefreshNeeded));
	}

	public updateImportPartnerPortfoliosResolvedAssets(resolvedAssets: Map<string, Partial<PasAsset>>) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosResolvedAssets(resolvedAssets));
	}

	public updateImportPartnerPortfoliosAccountErrorMap(accountErrorMap: Map<string, AccountErrors>) {
		return this.store.dispatch(new appAction.UpdateImportPartnerPortfoliosAccountErrorMap(accountErrorMap));
	}

	public getPartnerAuthenticationState(): Observable<PartnerAuthentication> {
		return this.store.pipe(select(appStateSelectors.getPartnerAuthenticationState));
	}

	public updatePartnerAuthenticationInfo(partnerId: string, partnerAuthenticationInfo: PartnerAuthenticationInfo) {
		return this.store.dispatch(new appAction.UpdatePartnerAuthenticationInfo({ partnerId, partnerAuthenticationInfo }));
	}

	public getPartnerAcknowledgementState(): Observable<PartnerAcknowledgement> {
		return this.store.pipe(select(appStateSelectors.getPartnerAcknowledgementState));
	}

	public updatePartnerAcknowledgementState(partnerAcknowledgement: PartnerAcknowledgement) {
		return this.store.dispatch(new appAction.UpdatePartnerAcknowledgementState(partnerAcknowledgement));
	}
}
